import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
    Loader2,
    Plus,
    Trash2,
    Save,
    Megaphone,
    Eye,
    EyeOff,
    Info,
    CheckCircle2,
    AlertTriangle,
    AlertOctagon,
    Sparkles,
} from "lucide-react";

const VARIANTS = [
    { id: "primary", label: "Żółty (marka)", Icon: Sparkles, color: "#facc15" },
    { id: "info", label: "Info (niebieski)", Icon: Info, color: "#38bdf8" },
    { id: "success", label: "Sukces (zielony)", Icon: CheckCircle2, color: "#22c55e" },
    { id: "warning", label: "Ostrzeżenie (pomarańcz)", Icon: AlertTriangle, color: "#fb923c" },
    { id: "danger", label: "Alert (czerwony)", Icon: AlertOctagon, color: "#ef4444" },
];

/**
 * Convert an ISO 8601 datetime string (possibly with timezone) to the local
 * `YYYY-MM-DDTHH:mm` format required by <input type="datetime-local">.
 * Returns empty string on invalid/missing input.
 */
function isoToLocalInput(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "";
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
        return "";
    }
}

/**
 * Convert a `YYYY-MM-DDTHH:mm` (local time) into a full ISO 8601 string
 * with timezone offset so backends and other browsers interpret it correctly.
 */
function localInputToIso(local) {
    if (!local) return "";
    try {
        const d = new Date(local);
        if (Number.isNaN(d.getTime())) return "";
        return d.toISOString();
    } catch {
        return "";
    }
}

function emptyDraft() {
    return {
        text: "",
        link_url: "",
        link_label: "",
        variant: "primary",
        active: true,
        priority: 0,
        countdown_to: "",
        countdown_label: "",
        notify: false,
    };
}

export default function AnnouncementsManager() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [draft, setDraft] = useState(emptyDraft());
    const [editingId, setEditingId] = useState(null);

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const { data } = await api.get("/admin/announcements");
            setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const resetForm = () => {
        setDraft(emptyDraft());
        setEditingId(null);
    };

    const save = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        setError("");
        setSuccess("");
        try {
            const payload = {
                text: (draft.text || "").trim(),
                link_url: (draft.link_url || "").trim(),
                link_label: (draft.link_label || "").trim(),
                variant: draft.variant || "primary",
                active: !!draft.active,
                priority: Number(draft.priority) || 0,
                countdown_to: localInputToIso((draft.countdown_to || "").trim()),
                countdown_label: (draft.countdown_label || "").trim(),
            };
            if (!payload.text) {
                setError("Treść ogłoszenia jest wymagana");
                setSaving(false);
                return;
            }
            if (editingId) {
                await api.put(`/announcements/${editingId}`, payload);
                setSuccess("Zaktualizowano ogłoszenie");
            } else {
                // notify=true → backend broadcasts a Web Push to all subscribers
                // right after inserting the announcement. Opt-in per creation so
                // the admin can add cosmetic tweaks without spamming phones.
                const q = draft.notify ? "?notify=true" : "";
                const { data: created } = await api.post(`/announcements${q}`, payload);
                setSuccess(
                    draft.notify
                        ? `Dodano ogłoszenie i wysłano powiadomienia push (id: ${created?.id?.slice(0, 8) || ""})`
                        : "Dodano ogłoszenie"
                );
            }
            resetForm();
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    const editItem = (item) => {
        setDraft({
            text: item.text || "",
            link_url: item.link_url || "",
            link_label: item.link_label || "",
            variant: item.variant || "primary",
            active: !!item.active,
            priority: Number(item.priority) || 0,
            countdown_to: isoToLocalInput(item.countdown_to || ""),
            countdown_label: item.countdown_label || "",
        });
        setEditingId(item.id);
        setSuccess("");
        setError("");
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const toggleActive = async (item) => {
        try {
            await api.put(`/announcements/${item.id}`, { active: !item.active });
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const remove = async (item) => {
        if (!window.confirm(`Usunąć ogłoszenie: "${item.text.slice(0, 60)}"?`)) return;
        try {
            await api.delete(`/announcements/${item.id}`);
            if (editingId === item.id) resetForm();
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const activeCount = useMemo(() => items.filter((i) => i.active).length, [items]);
    const variantMeta = (id) => VARIANTS.find((v) => v.id === id) || VARIANTS[0];

    return (
        <div className="space-y-6" data-testid="admin-announcements">
            {/* Form */}
            <form
                onSubmit={save}
                className="panel-yellow p-6 space-y-4"
                data-testid="announcement-form"
            >
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 label-eyebrow">
                        <Megaphone size={14} />
                        {editingId ? "Edytujesz ogłoszenie" : "Nowe ogłoszenie"}
                    </div>
                    {editingId && (
                        <button
                            type="button"
                            onClick={resetForm}
                            className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-secondary)] hover:text-white"
                        >
                            Anuluj edycję
                        </button>
                    )}
                </div>

                <label className="block">
                    <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                        Treść ogłoszenia
                    </span>
                    <textarea
                        data-testid="announcement-text"
                        value={draft.text}
                        onChange={(e) => setDraft((d) => ({ ...d, text: e.target.value }))}
                        rows={2}
                        maxLength={280}
                        placeholder="np. Premiera wersji 1.2 - 10 sierpnia!"
                        className="input"
                    />
                    <span className="block text-[10px] text-[color:var(--text-muted)] mt-1">
                        {draft.text.length}/280 znaków - banner jest wąski, krótkie komunikaty
                        wyglądają najlepiej.
                    </span>
                </label>

                <div className="grid sm:grid-cols-2 gap-4">
                    <label className="block">
                        <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                            Link (opcjonalny)
                        </span>
                        <input
                            data-testid="announcement-link-url"
                            value={draft.link_url}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, link_url: e.target.value }))
                            }
                            placeholder="/#pobierz lub https://..."
                            className="input"
                        />
                    </label>
                    <label className="block">
                        <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                            Tekst przycisku (jeśli link)
                        </span>
                        <input
                            data-testid="announcement-link-label"
                            value={draft.link_label}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, link_label: e.target.value }))
                            }
                            placeholder="np. Zobacz szczegóły"
                            className="input"
                            maxLength={40}
                        />
                    </label>
                </div>

                <div className="grid sm:grid-cols-3 gap-4">
                    <label className="block sm:col-span-2">
                        <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                            Kolor / wariant
                        </span>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                            {VARIANTS.map((v) => {
                                const active = draft.variant === v.id;
                                const Icon = v.Icon;
                                return (
                                    <button
                                        type="button"
                                        key={v.id}
                                        data-testid={`announcement-variant-${v.id}`}
                                        onClick={() =>
                                            setDraft((d) => ({ ...d, variant: v.id }))
                                        }
                                        className={`flex flex-col items-center gap-1 p-2 rounded-md border text-[10px] uppercase tracking-wider transition-colors ${
                                            active
                                                ? "border-white/40 bg-white/10 text-white"
                                                : "border-white/10 text-[color:var(--text-secondary)] hover:border-white/20 hover:text-white"
                                        }`}
                                        style={{
                                            color: active ? v.color : undefined,
                                        }}
                                    >
                                        <Icon size={14} style={{ color: v.color }} />
                                        <span className="text-[9px] leading-tight text-center">
                                            {v.label.split(" (")[0]}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </label>

                    <label className="block">
                        <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                            Priorytet (wyższy = wyżej)
                        </span>
                        <input
                            type="number"
                            data-testid="announcement-priority"
                            value={draft.priority}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, priority: e.target.value }))
                            }
                            className="input"
                        />
                    </label>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                    <label className="block">
                        <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                            Odliczanie do (opcjonalne)
                        </span>
                        <input
                            type="datetime-local"
                            data-testid="announcement-countdown-to"
                            value={draft.countdown_to}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, countdown_to: e.target.value }))
                            }
                            className="input"
                        />
                        <span className="block text-[10px] text-[color:var(--text-muted)] mt-1">
                            Ustaw datę i godzinę - na banerze pojawi się timer w dniach,
                            godzinach, minutach i sekundach. Zostaw puste, żeby wyłączyć.
                        </span>
                    </label>
                    <label className="block">
                        <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                            Etykieta obok timera (opcj.)
                        </span>
                        <input
                            data-testid="announcement-countdown-label"
                            value={draft.countdown_label}
                            onChange={(e) =>
                                setDraft((d) => ({
                                    ...d,
                                    countdown_label: e.target.value,
                                }))
                            }
                            placeholder="np. do premiery"
                            className="input"
                            maxLength={40}
                        />
                        {draft.countdown_to && (
                            <button
                                type="button"
                                onClick={() =>
                                    setDraft((d) => ({
                                        ...d,
                                        countdown_to: "",
                                        countdown_label: "",
                                    }))
                                }
                                className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-secondary)] hover:text-white"
                            >
                                Wyczyść odliczanie
                            </button>
                        )}
                    </label>
                </div>

                <label className="flex items-center gap-3 p-3 rounded-md border border-white/10 bg-[color:var(--bg-base)] cursor-pointer select-none">
                    <input
                        type="checkbox"
                        data-testid="announcement-active"
                        checked={!!draft.active}
                        onChange={(e) =>
                            setDraft((d) => ({ ...d, active: e.target.checked }))
                        }
                        className="h-4 w-4 accent-[color:var(--primary)]"
                    />
                    <span className="text-sm text-white">
                        Aktywne - wyświetlaj publicznie
                    </span>
                </label>

                {!editingId && (
                    <label className="flex items-start gap-3 p-3 rounded-md border border-[color:var(--primary)]/25 bg-[color:var(--primary)]/5 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            data-testid="announcement-notify"
                            checked={!!draft.notify}
                            onChange={(e) =>
                                setDraft((d) => ({ ...d, notify: e.target.checked }))
                            }
                            className="h-4 w-4 mt-0.5 accent-[color:var(--primary)]"
                        />
                        <span className="text-sm text-white flex-1">
                            Wyślij powiadomienie push do subskrybentów
                            <span className="block text-[11px] text-[color:var(--text-secondary)] mt-1">
                                Zaznacz, żeby razem z ogłoszeniem wysłać
                                natywne powiadomienie na telefony / komputery
                                użytkowników którzy je włączyli.
                            </span>
                        </span>
                    </label>
                )}

                {error && (
                    <div className="text-sm text-red-400" data-testid="announcement-error">
                        {error}
                    </div>
                )}
                {success && (
                    <div
                        className="text-sm text-[color:var(--primary)]"
                        data-testid="announcement-success"
                    >
                        {success}
                    </div>
                )}

                <button
                    type="submit"
                    data-testid="announcement-save"
                    disabled={saving}
                    className="btn-primary w-full disabled:opacity-60"
                >
                    {saving ? (
                        <Loader2 size={14} className="animate-spin" />
                    ) : editingId ? (
                        <Save size={14} />
                    ) : (
                        <Plus size={14} />
                    )}
                    {editingId ? "Zapisz zmiany" : "Dodaj ogłoszenie"}
                </button>
            </form>

            {/* List */}
            <div className="space-y-3">
                <div className="flex items-center justify-between text-xs text-[color:var(--text-secondary)]">
                    <span>
                        Wszystkie ogłoszenia:{" "}
                        <strong className="text-white">{items.length}</strong> · aktywne:{" "}
                        <strong className="text-[color:var(--primary)]">{activeCount}</strong>
                    </span>
                </div>

                {loading ? (
                    <div className="text-sm text-[color:var(--text-secondary)] flex items-center gap-2">
                        <Loader2 size={14} className="animate-spin" /> Ładowanie…
                    </div>
                ) : items.length === 0 ? (
                    <div className="p-6 rounded-lg border border-dashed border-white/10 text-sm text-[color:var(--text-muted)] text-center">
                        Brak ogłoszeń. Dodaj pierwsze powyżej - pojawi się jako pasek u
                        góry strony.
                    </div>
                ) : (
                    <ul className="space-y-2" data-testid="announcement-list">
                        {items.map((item) => {
                            const v = variantMeta(item.variant);
                            const Icon = v.Icon;
                            return (
                                <li
                                    key={item.id}
                                    data-testid={`announcement-item-${item.id}`}
                                    className={`group flex items-start gap-3 p-4 rounded-lg border transition-colors ${
                                        item.active
                                            ? "border-white/15 bg-[color:var(--bg-surface)]"
                                            : "border-white/5 bg-[color:var(--bg-surface)]/40 opacity-70"
                                    }`}
                                >
                                    <span
                                        className="inline-flex items-center justify-center w-8 h-8 rounded-full shrink-0"
                                        style={{
                                            background: `${v.color}22`,
                                            color: v.color,
                                        }}
                                    >
                                        <Icon size={14} />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-white leading-snug">
                                            {item.text}
                                        </div>
                                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-muted)]">
                                            <span>
                                                Priorytet:{" "}
                                                <span className="text-white">
                                                    {item.priority}
                                                </span>
                                            </span>
                                            <span>{v.label.split(" (")[0]}</span>
                                            {item.link_url && (
                                                <span className="normal-case tracking-normal text-[color:var(--text-secondary)]">
                                                    → {item.link_label || item.link_url}
                                                </span>
                                            )}
                                            <span
                                                className={
                                                    item.active
                                                        ? "text-[color:var(--primary)]"
                                                        : "text-[color:var(--text-muted)]"
                                                }
                                            >
                                                {item.active ? "Aktywne" : "Nieaktywne"}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button
                                            data-testid={`announcement-toggle-${item.id}`}
                                            onClick={() => toggleActive(item)}
                                            title={item.active ? "Ukryj" : "Pokaż"}
                                            className="p-2 rounded-md text-[color:var(--text-secondary)] hover:text-white hover:bg-white/5"
                                        >
                                            {item.active ? (
                                                <Eye size={14} />
                                            ) : (
                                                <EyeOff size={14} />
                                            )}
                                        </button>
                                        <button
                                            data-testid={`announcement-edit-${item.id}`}
                                            onClick={() => editItem(item)}
                                            title="Edytuj"
                                            className="p-2 rounded-md text-[color:var(--text-secondary)] hover:text-white hover:bg-white/5"
                                        >
                                            <Save size={14} />
                                        </button>
                                        <button
                                            data-testid={`announcement-delete-${item.id}`}
                                            onClick={() => remove(item)}
                                            title="Usuń"
                                            className="p-2 rounded-md text-red-400 hover:bg-red-400/10"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            <style>{`
                .input { width:100%; background: var(--bg-base); border:1px solid rgba(255,255,255,0.1); border-radius:6px; padding:8px 12px; color:white; font-size:14px; }
                .input:focus { outline:none; border-color: var(--primary); }
            `}</style>
        </div>
    );
}
